import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Grid, GridItem, Heading, Image, Spinner, Text } from '@chakra-ui/react';
import { SystemTranslationKeys } from '@common/entities';
import { queryClient } from 'AppWrapper';
import { RootState } from 'state/store';

import { NoFloorPlanImage } from 'assets/images';
import { useSystemTranslation } from 'hooks';
import { useLocale } from 'hooks/utils/useLocale';
import { getFloorPlans } from 'services';
import { FloorPlan } from 'types/floorplan-page-types';

const WaitListCard: React.FC = () => {
  const { t, tSystem } = useSystemTranslation();
  const locale = useLocale();
  const { waitlistSelections, moveInDate } = useSelector((state: RootState) => state.unitSelection);
  const { propertyId } = useSelector((state: RootState) => state.application.applicant);
  const { floorplanIds } = waitlistSelections;
  const [floorPlans, setFloorPlans] = useState<FloorPlan[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFloorPlans = useCallback(async () => {
    if (!propertyId || !moveInDate) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await queryClient.fetchQuery({
        queryKey: ['floor-plans', propertyId, moveInDate, locale],
        queryFn: () => getFloorPlans(propertyId, moveInDate),
      });
      setFloorPlans(data);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_err) {
      setError(
        t(
          SystemTranslationKeys.cart.failedToLoadFloorPlans,
          'Failed to load floor plans. Please try again later.',
        ),
      );
    } finally {
      setIsLoading(false);
    }
  }, [locale, propertyId, moveInDate, t]);

  useEffect(() => {
    fetchFloorPlans();
  }, [fetchFloorPlans]);

  const filteredFloorPlans = floorPlans.filter((floorPlan) => floorplanIds.includes(floorPlan.id));

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        p={4}
        mt={3}
        border="1px solid"
        borderColor="silver.highlight"
        borderRadius="16px"
        boxShadow="0px 1px 3px rgba(0, 0, 0, 0.15)"
      >
        <Spinner data-testid="loading-spinner" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4} color="red.500">
        <Text>{error}</Text>
      </Box>
    );
  }

  if (filteredFloorPlans.length === 0) {
    return null;
  }

  return (
    <Box
      p={2}
      mt={3}
      border="1px solid"
      borderColor="silver.highlight"
      borderRadius="16px"
      boxShadow="0px 1px 3px rgba(0, 0, 0, 0.15)"
      data-testid="waitlist-card"
    >
      {filteredFloorPlans.map((floorPlan) => (
        <Grid key={`waitlist-card-${floorPlan.id}`} gap={2} templateColumns="repeat(3, 1fr)">
          <GridItem colSpan={1} position="relative">
            <Image
              src={floorPlan.media[0]?.fullsizeUri || NoFloorPlanImage}
              alt={floorPlan.media[0]?.caption ?? 'floor-plan'}
              width="100%"
              height="100%"
              objectFit="contain"
              borderLeftRadius="12px"
              borderRightRadius="4px"
              fallbackSrc={NoFloorPlanImage}
            />
          </GridItem>
          <GridItem colSpan={2} pl={1}>
            <Heading as="h5" size="sm" my={1}>
              {tSystem(SystemTranslationKeys.cart.floorplan)}: {floorPlan.name}
            </Heading>
            <Text fontSize="xs" as="em">
              {tSystem(SystemTranslationKeys.cart.waitlisted)}
            </Text>
          </GridItem>
        </Grid>
      ))}
    </Box>
  );
};

export default WaitListCard;
